import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import OfferCalendarMonthPicker from '../CalendarPicker/MonthPicker/OfferCalendarMonthPicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarSidebarDayPicker from './OfferCalendarSidebarDayPicker'
import { getMultiRoomCalendarMonths, getMultiRoomDaysOtherAvailability, getSelectedOfferPageMultiRoomDay, isMultiRoomFetchingCalendars } from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferCalendarNotAvailableMessage from '../CalendarPicker/OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from '../CalendarPicker/OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarRoomSelect from '../CalendarPicker/CalendarRooms/OfferCalendarRoomSelect'
import OfferCalendarDurationSelect from '../CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import OfferCalendarFlightsToggle from '../CalendarPicker/OfferCalendarFlightsToggle'
import noop from 'lib/function/noop'
import OfferCalendarCapacitySelect from '../CalendarPicker/CalendarCapacity/OfferCalendarCapacitySelect'
import { nonNullable } from 'lib/array/arrayUtils'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferCalendarSidebarDatePickerFooter from './OfferCalendarSidebarDatePickerFooter'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCalendarAirportSelect from '../CalendarPicker/CalendarAirport/OfferCalendarAirportSelect'
import OfferCalendarRoomLineSelect from '../CalendarPicker/CalendarRooms/OfferCalendarRoomLineSelect'
import * as Analytics from 'analytics/analytics'
import OfferCalendarflightDealBanner from '../CalendarPicker/OfferCalendarFlightDealBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCalendarMonths, getDaysOtherAvailability, getSelectedOfferPageDay, isFetchingCalendars } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import OfferCalendarConfirmDatesFooter from './OfferCalendarConfirmDatesFooter'

const DaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

interface Props {
  currentOfferId: string
  offer: App.Offer
  airport: App.Airport
  fetching?: boolean
  checkIn?: string
  selectedDay?: App.CalendarDay
  calendarError?: boolean
  calendarAvailability: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  onBack?: () => void
  onClose?: () => void
  subFilters?: Array<OfferPageFilterType>
  isLuxPlusEnabled: boolean
  room?: App.PackageRoomType
  isAnyFlightsActive: boolean
  useConfirmDatesFooter?: boolean
  isMultiRoom: boolean
  otherAvailability: { [checkIn: string]: boolean }
}

const defaultSubfilters: Array<OfferPageFilterType> = ['duration', 'flights-toggle', 'room']
function OfferCalendarSidebarDatePicker(props: Props) {
  const {
    offer,
    fetching,
    selectedDay,
    checkIn,
    calendarError,
    calendarAvailability,
    defaultMonth,
    onBack,
    onClose = noop,
    currentOfferId,
    subFilters = defaultSubfilters,
    isLuxPlusEnabled,
    airport,
    isAnyFlightsActive,
    useConfirmDatesFooter,
    isMultiRoom,
    otherAvailability,
  } = props

  const { currentRegionHasFlights } = useContext(GeoContext)
  const modalContext = useModalElementContext()

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const isMobileScreen = useScreenSizeOnly('mobile')
  const useNewMobileHotelOfferPage = isMobileScreen && offer.type === OFFER_TYPE_HOTEL

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  useEffect(() => {
    if (checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [checkIn, dispatch, selectedDay, currentOfferId, otherAvailability])

  const onBackToMonth = useCallback(() => {
    scrollerRef.current?.scrollTo({ top: 0 })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    if (!useNewMobileHotelOfferPage) {
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
    setMonth(undefined)
  }, [onUserAnalyticsAction, useNewMobileHotelOfferPage, dispatch, currentOfferId])

  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly

  const hasSelectedMonth = selectedMonth !== undefined

  const capacities = useMemo(() => nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities)), [offer])

  const onModalClose = useCallback(() => {
    onClose()
    modalContext.resolve()
  }, [onClose, modalContext])

  const onModalBack = useCallback(() => {
    if (selectedMonth) {
      onBackToMonth()
    } else {
      onBack?.()
    }
  }, [onBack, onBackToMonth, selectedMonth])

  const onMonthSelect = useCallback((monthKey: string) => {
    scrollerRef.current?.scrollTo({ top: 0 })
    setMonth(monthKey)
  }, [])

  useEffect(() => {
    if (offer.luxPlus.hasMemberPrices && isLuxPlusEnabled) {
      dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
    }
  }, [dispatch, isLuxPlusEnabled, offer])

  const showBack = !!onBack || !!selectedMonth

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: hasSelectedMonth ? 'select_day' : 'select_month',
      action: 'appear',
      type: 'interaction',
      category: 'offer_funnel',
    })
  }, [hasSelectedMonth])

  return (<>
    <ModalHeader
      title="Select your dates"
      onCloseButtonClick={onModalClose}
      onBackButtonClick={showBack ? onModalBack : undefined}
    >
      <Group direction="vertical" gap={20}>
        <HorizontalContainerWithFadedOverflow overflowGutter={20} gap={8}>
          {isLuxPlusEnabled && offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
          {subFilters.includes('duration') && <OfferCalendarDurationSelect variant="modal" />}
          {useNewMobileHotelOfferPage && !isMultiRoom && subFilters.includes('room') && <OfferCalendarRoomLineSelect variant="modal" />}
          {subFilters.includes('capacity') && <OfferCalendarCapacitySelect variant="modal" capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
          {!useNewMobileHotelOfferPage && subFilters.includes('room') && <OfferCalendarRoomSelect variant="modal" />}
          {flightsEnabled && subFilters.includes('flights-toggle') && <OfferCalendarFlightsToggle />}
          {flightsEnabled && isAnyFlightsActive && subFilters.includes('airport') && <OfferCalendarAirportSelect />}
        </HorizontalContainerWithFadedOverflow>
        {hasSelectedMonth && !fetching && (
          <DaysHeader>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Monday">Mon</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Tuesday">Tue</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Wednesday">Wed</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Thursday">Thu</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Friday">Fri</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Saturday">Sat</Caption>
            <Caption align="center" colour="neutral-three" variant="large" as="abbr" title="Sunday">Sun</Caption>
          </DaysHeader>
        )}
      </Group>
    </ModalHeader>
    <ModalBody ref={scrollerRef}>
      <ModalContent>
        <LoadingIndicator floating visible={fetching && hasSelectedMonth} />
        {!fetching && (calendarError || !isAnyFlightsActive) && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
        {!fetching && !calendarError && isAnyFlightsActive && !calendarAvailability && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}

        {calendarAvailability && <VerticalSpacer gap={8}>
          <OfferCalendarflightDealBanner
            destinationAirportCode={offer.flightDestinationPort}
            originAirportCode={airport.code}
            isFlightsActive={isAnyFlightsActive}
            showCountdown={false}
          />

          {hasSelectedMonth && !fetching && <OfferCalendarSidebarDayPicker
            onBackToMonth={onBackToMonth}
            initialMonth={selectedMonth}
            flightsOnly={offer.bundledWithFlightsOnly}
            contentRef={scrollerRef}
          />}

          {!hasSelectedMonth && <OfferCalendarMonthPicker
            onMonthSelect={onMonthSelect}
          />}
        </VerticalSpacer>}
      </ModalContent>
    </ModalBody>
    {!useConfirmDatesFooter && <OfferCalendarSidebarDatePickerFooter />}
    {useConfirmDatesFooter && <OfferCalendarConfirmDatesFooter onApply={onModalClose} />}
  </>)
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    currentOfferId: state.paginator?.currentOfferId,
    airport: state.airport,
    calendarAvailability: isMultiRoom ? getMultiRoomCalendarMonths(state, appState).availableCount > 0 : getCalendarMonths(appState, state).availableCount > 0,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    fetching: isMultiRoom ? isMultiRoomFetchingCalendars(state, appState) : isFetchingCalendars(appState, state),
    offer: state.offer,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
    isAnyFlightsActive: state.flights,
    isMultiRoom,
  }
}, OfferCalendarSidebarDatePicker)
